import { cn } from '@/lib/utils'
import { ArrowUpRight } from 'lucide-react'
import Link from 'next/link'

export type THomeSectionTab = {
  key: string
  label: string
}

type Props = {
  title: string
  tabs?: THomeSectionTab[]
  activeTab?: string
  onChangeTab?: (key: string) => void
  viewAllHref?: string
  viewAllLabel?: string
  gaTrackingPrefix: string
  // Mobile: đặt link "Xem tất cả" xuống dưới tiêu đề (khi không có tab)
  stackOnMobile?: boolean
  className?: string
}

/**
 * Header dùng chung cho các section trang chủ: tiêu đề + tab gạch chân + link "Xem tất cả".
 * Desktop: tiêu đề và tab cùng hàng, link bên phải. Mobile: tab và link xuống hàng dưới tiêu đề.
 */
export const HomeSectionHeader = ({
  title,
  tabs,
  activeTab,
  onChangeTab,
  viewAllHref,
  viewAllLabel,
  gaTrackingPrefix,
  stackOnMobile,
  className,
}: Props) => {
  const hasTabs = !!tabs?.length

  const viewAll =
    viewAllHref && viewAllLabel ? (
      <Link
        href={viewAllHref}
        className='flex shrink-0 items-center gap-1 border-b border-neutral-800 py-1.5 text-sm font-medium leading-5 text-neutral-800 lg:gap-2.5 lg:px-4 lg:py-2 lg:text-base lg:leading-6'
        ga-tracking-value={`${gaTrackingPrefix}__view-all`}
        ga-tracking-label={viewAllLabel}
      >
        {viewAllLabel}
        <ArrowUpRight className='size-4 lg:size-6' strokeWidth={1.5} />
      </Link>
    ) : null

  return (
    <div
      className={cn(
        'flex justify-between gap-3 lg:items-end',
        hasTabs ? 'max-lg:flex-col' : 'items-end',
        !hasTabs && stackOnMobile && 'max-lg:flex-col max-lg:items-start',
        className
      )}
    >
      <div className='flex flex-col gap-3 lg:flex-row lg:items-end lg:gap-10'>
        <h2 className='font-criteria text-2xl font-semibold leading-[35px] tracking--2 text-neutral-900 lg:text-[55px] lg:font-medium lg:leading-[76px]'>
          {title}
        </h2>
        {hasTabs && (
          <div className='flex items-end justify-between lg:pb-1'>
            <div className='flex gap-5 lg:gap-4' role='tablist'>
              {tabs.map((tab) => {
                const isActive = tab.key === activeTab
                return (
                  <button
                    key={tab.key}
                    type='button'
                    role='tab'
                    aria-selected={isActive}
                    onClick={() => onChangeTab?.(tab.key)}
                    className={cn(
                      'border-b-2 py-2 text-sm font-medium leading-5 transition-colors lg:px-6',
                      isActive
                        ? 'border-neutral-800 text-neutral-900'
                        : 'border-transparent text-neutral-500 hover:text-neutral-900'
                    )}
                    ga-tracking-value={`${gaTrackingPrefix}__tab__${tab.key}`}
                    ga-tracking-label={tab.label}
                  >
                    {tab.label}
                  </button>
                )
              })}
            </div>
            {viewAll && <div className='pb-1 lg:hidden'>{viewAll}</div>}
          </div>
        )}
      </div>
      {viewAll && (
        <div className={cn('pb-1', hasTabs && 'max-lg:hidden')}>{viewAll}</div>
      )}
    </div>
  )
}
